<template>
  <div class="pml-wrap">
    <div class="pml-card">

      <!-- Loading / verifying -->
      <template v-if="step === 'loading'">
        <div class="pml-icon">
          <div class="pml-spinner"></div>
        </div>
        <div class="pml-title">Verifying your link…</div>
        <div class="pml-sub">Please wait while we validate your access link.</div>
        <div class="pml-dots"><span></span><span></span><span></span></div>
      </template>

      <!-- Set password form -->
      <template v-else-if="step === 'set-password'">
        <div class="pml-icon success-ring">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m0 0v2m0-2h2m-2 0H10m9-7V8a7 7 0 10-14 0v2M5 21h14a1 1 0 001-1v-5a1 1 0 00-1-1H5a1 1 0 00-1 1v5a1 1 0 001 1z"/>
          </svg>
        </div>
        <div class="pml-title">Set your password</div>
        <div class="pml-sub">Welcome, {{ partnerName }}! Choose a password to secure your partner account.</div>

        <form @submit.prevent="doSetPassword" class="pml-form">
          <div class="pml-field">
            <label>New password</label>
            <div class="pml-input-wrap">
              <input
                v-model="newPassword"
                :type="showNew ? 'text' : 'password'"
                placeholder="Minimum 8 characters"
                autocomplete="new-password"
                @paste.prevent
                @copy.prevent
                required
              />
              <button type="button" class="eye-btn" @click="showNew = !showNew" tabindex="-1">
                <svg v-if="showNew" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Strength checklist -->
          <div v-if="newPassword" class="pml-rules">
            <div v-for="rule in passwordStrengthRules" :key="rule.label" class="pml-rule" :class="{ met: rule.met }">
              <svg viewBox="0 0 12 12" fill="none">
                <template v-if="rule.met">
                  <circle cx="6" cy="6" r="5.5" stroke="#0D9488" fill="#0D9488" fill-opacity="0.12"/>
                  <path d="M3.5 6l1.8 1.8L8.5 4.5" stroke="#0D9488" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                </template>
                <template v-else>
                  <circle cx="6" cy="6" r="5.5" stroke="#D1D5DB"/>
                </template>
              </svg>
              <span>{{ rule.label }}</span>
            </div>
          </div>

          <div class="pml-field">
            <label>Confirm password</label>
            <div class="pml-input-wrap">
              <input
                v-model="confirmPassword"
                :type="showConfirm ? 'text' : 'password'"
                placeholder="Re-enter your password"
                autocomplete="new-password"
                @paste.prevent
                @copy.prevent
                required
              />
              <button type="button" class="eye-btn" @click="showConfirm = !showConfirm" tabindex="-1">
                <svg v-if="showConfirm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </button>
            </div>
          </div>

          <div v-if="formError" class="pml-error">{{ formError }}</div>

          <button type="submit" class="pml-btn" :disabled="submitting || !allRulesMet">
            <span v-if="submitting">Setting password…</span>
            <span v-else>Set Password &amp; Continue</span>
          </button>
        </form>
      </template>

      <!-- Success -->
      <template v-else-if="step === 'done'">
        <div class="pml-icon success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div class="pml-title">Password set!</div>
        <div class="pml-sub">Redirecting you to your dashboard…</div>
        <div class="pml-dots"><span></span><span></span><span></span></div>
      </template>

      <!-- Error -->
      <template v-else-if="step === 'error'">
        <div class="pml-icon error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <div class="pml-title">Link invalid or expired</div>
        <div class="pml-sub">{{ errorMsg }}</div>
        <a href="/login" class="pml-back">← Back to Login</a>
      </template>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PartnerMagicLogin',

  data() {
    return {
      step:            'loading',   // loading | set-password | done | error
      partnerName:     '',
      magicJwt:        '',
      magicData:       null,
      newPassword:     '',
      confirmPassword: '',
      showNew:         false,
      showConfirm:     false,
      submitting:      false,
      formError:       '',
      errorMsg:        'This link may have expired or already been used.',
    };
  },

  created() {
    this.verifyToken();
  },

  computed: {
    passwordStrengthRules() {
      const p = this.newPassword || '';
      const ref = (this.magicData?.email || '').toLowerCase().split('@')[0] || '';
      const blocked = ['password','qwerty','letmein','welcome','monkey','iloveyou','sunshine','princess','football','dragon','trustno1','passw0rd','admin','starr','starrboard','tectalik'];
      const seqs = ['0123','1234','2345','3456','4567','5678','6789','7890','9876','8765','7654','6543','5432','4321','3210'];
      const lower = p.toLowerCase();
      return [
        { label: 'At least 8 characters',          met: p.length >= 8 },
        { label: 'One uppercase letter',            met: /[A-Z]/.test(p) },
        { label: 'One lowercase letter',            met: /[a-z]/.test(p) },
        { label: 'One number',                      met: /\d/.test(p) },
        { label: 'One special character',           met: /[^A-Za-z0-9]/.test(p) },
        { label: 'No sequential numbers (1234…)',   met: p.length > 0 && !seqs.some(s => p.includes(s)) },
        { label: 'No common words (admin, starr…)', met: p.length > 0 && !blocked.some(w => lower.includes(w)) },
        { label: 'Not your email / username',       met: p.length > 0 && ref.length > 0 ? !lower.includes(ref) : p.length > 0 },
      ];
    },
    allRulesMet() {
      return this.passwordStrengthRules.every(r => r.met);
    },
  },

  methods: {
    async verifyToken() {
      const token = this.$route.query.token;
      if (!token) {
        this.step     = 'error';
        this.errorMsg = 'No access token found. Please use the link from your welcome email.';
        return;
      }

      // Remove token from browser history immediately
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, '/partner_magic');
      }

      try {
        const res = await this.$papi.get('public/partner_magic_login', { params: { token } });
        const data = res.data;

        if (data.result !== 'success') {
          throw new Error(data.error || 'Invalid or expired link.');
        }

        this.magicJwt    = data.token;
        this.magicData   = data;
        this.partnerName = data.partner_name || data.name || '';
        this.step        = 'set-password';

      } catch (err) {
        this.step     = 'error';
        this.errorMsg = err.response?.data?.error || err.message || 'This link may have expired or already been used.';
      }
    },

    async doSetPassword() {
      this.formError = '';

      if (!this.allRulesMet) {
        this.formError = 'Please meet all password requirements before continuing.';
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.formError = 'Passwords do not match.';
        return;
      }

      this.submitting = true;
      try {
        await this.$papi.post(
          'public/partner_set_password',
          { new_password: this.newPassword },
          { headers: { Authorization: `Bearer ${this.magicJwt}` } }
        );

        // Save partner session to localStorage
        const d = this.magicData;
        localStorage.setItem('PARTNER_TOKEN',   d.token);
        localStorage.setItem('PARTNER_ID',      String(d.partner_id   || ''));
        localStorage.setItem('PARTNER_NAME',    d.partner_name || d.name || '');
        localStorage.setItem('PARTNER_COMPANY', d.company_name || '');
        localStorage.setItem('PARTNER_EMAIL',   d.email        || '');

        this.step = 'done';
        setTimeout(() => this.$router.replace('/partner_landing_v2'), 1200);

      } catch (err) {
        this.formError = err.response?.data?.error || 'Could not set password. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.pml-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%);
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}
.pml-card {
  background: #fff;
  border-radius: 20px;
  padding: 48px 40px 40px;
  text-align: center;
  box-shadow: 0 8px 40px rgba(59, 76, 192, 0.10);
  max-width: 440px;
  width: 90%;
}
.pml-icon {
  width: 72px; height: 72px; border-radius: 50%;
  background: #EEF0FD; display: flex; align-items: center;
  justify-content: center; margin: 0 auto 24px; transition: background 0.3s;
}
.pml-icon svg { width: 32px; height: 32px; color: #3B4CC0; }
.pml-icon.success { background: #DCFCE7; }
.pml-icon.success svg { color: #16a34a; }
.pml-icon.success-ring { background: #eff6ff; }
.pml-icon.success-ring svg { color: #1a3a5c; }
.pml-icon.error { background: #FEE2E2; }
.pml-icon.error svg { color: #dc2626; }
.pml-spinner {
  width: 32px; height: 32px; border: 3px solid #C7CCEF;
  border-top-color: #3B4CC0; border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.pml-title { font-size: 1.25rem; font-weight: 700; color: #1E1E2F; margin-bottom: 8px; }
.pml-sub { font-size: 0.875rem; color: #9A96A8; margin-bottom: 20px; }
.pml-dots { display: flex; justify-content: center; gap: 6px; margin-top: 8px; }
.pml-dots span {
  width: 8px; height: 8px; border-radius: 50%; background: #C7CCEF;
  animation: bounce 1.2s ease-in-out infinite;
}
.pml-dots span:nth-child(1) { animation-delay: 0s; }
.pml-dots span:nth-child(2) { animation-delay: 0.2s; }
.pml-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
  40%           { transform: scale(1.1); opacity: 1; }
}

/* Form */
.pml-form { text-align: left; margin-top: 20px; }
.pml-field { margin-bottom: 16px; }
.pml-field label {
  display: block; font-size: 0.82rem; font-weight: 600;
  color: #374151; margin-bottom: 6px;
}
.pml-input-wrap { position: relative; }
.pml-input-wrap input {
  width: 100%; padding: 10px 40px 10px 12px;
  border: 1.5px solid #d1d5db; border-radius: 8px;
  font-size: 0.9rem; outline: none; box-sizing: border-box;
  transition: border-color 0.2s;
}
.pml-input-wrap input:focus { border-color: #1a3a5c; }
.eye-btn {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; padding: 0;
  display: flex; align-items: center;
}
.eye-btn svg { width: 18px; height: 18px; color: #9ca3af; }
/* Password strength checklist */
.pml-rules {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
}
.pml-rule {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: #9ca3af;
  transition: color 0.2s;
}
.pml-rule svg {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}
.pml-rule.met {
  color: #0D9488;
}

.pml-error {
  background: #fee2e2; color: #b91c1c; border-radius: 6px;
  padding: 8px 12px; font-size: 0.82rem; margin-bottom: 12px;
}
.pml-btn {
  width: 100%; padding: 12px; background: #1a3a5c; color: #fff;
  border: none; border-radius: 8px; font-size: 0.95rem; font-weight: 600;
  cursor: pointer; transition: background 0.2s; margin-top: 4px;
}
.pml-btn:hover:not(:disabled) { background: #0f2440; }
.pml-btn:disabled { opacity: 0.65; cursor: not-allowed; }
.pml-back {
  display: inline-block; margin-top: 16px; font-size: 0.85rem;
  color: #3B4CC0; text-decoration: none; font-weight: 600;
}
.pml-back:hover { text-decoration: underline; }
</style>
